import { HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  inject,
  input,
  OnInit,
  output,
  OutputEmitterRef,
  signal,
  Signal,
  WritableSignal
} from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import posthog from 'posthog-js';
import { forkJoin, Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { ContactService } from '@customer/contact';
import { DocusignService, StorageService, ToastService } from '@core/services';
import { RegisterService } from '@customer/contact/register';
import { PermissionsService } from '@customer/services';
import { ContainsOneValidator, EmailExistsValidator } from '@core/validators';
import { SvgIconComponent } from 'angular-svg-icon';
import { Account } from '@legacy-models/portal/Account';
import { SessionAuth } from '@legacy-models/auth/user';
import { PortalRolesGroup, SanitizedContact, SanitizedPortalRole } from '../../SanitizedData';

interface AdminFormControl extends FormControl<boolean> {
  id?: string;
}
interface NewContactFormValue {
  email: string;
  firstName: string;
  lastName: string;
  portalRoles: boolean[];
}
interface AddContactResponse {
  ContactId?: string;
  UserMessage: string;
}
interface NewContact {
  businessSegments: string[];
  email: string;
  esaaStatus: 'yes' | 'no' | 'pending';
  id: string;
  isExistingContact: boolean;
  name: { first: string; full: string; last: string };
  segments: PortalRolesGroup[];
}
@Component({
  selector: 'oke-add-contact',
  templateUrl: './add-contact.html',
  styleUrl: './add-contact.scss',
  imports: [ReactiveFormsModule, SvgIconComponent]
})
export class AddContact implements OnInit {
  private readonly _contactSvc: ContactService = inject(ContactService);
  private readonly _docusignSvc: DocusignService = inject(DocusignService);
  private readonly _formBuilder: FormBuilder = inject(FormBuilder);
  private readonly _registerSvc: RegisterService = inject(RegisterService);
  private readonly _permissionsSvc: PermissionsService = inject(PermissionsService);
  private readonly _storageSvc: StorageService = inject(StorageService);
  private readonly _toastSvc: ToastService = inject(ToastService);
  readonly $accountContacts = input.required<SanitizedContact[]>({
    alias: 'accountContacts'
  });
  readonly $accountRoles = input<SanitizedPortalRole[], SanitizedPortalRole[]>([], {
    alias: 'accountRoles',
    transform: function (value: SanitizedPortalRole[]) {
      return value.filter(({ name }) => name.toLowerCase() !== 'account access');
    }
  });
  private readonly _$hasAccountSignedNewEsaa: WritableSignal<boolean> = signal<boolean>(true);
  private readonly _$hasAccountSentNewEsaa: WritableSignal<boolean> = signal<boolean>(false);
  private readonly _$esaaSigner = signal<SanitizedContact | null>(null);
  private readonly _$hasExistingUserSubmittedForm: WritableSignal<boolean> = signal<boolean>(false);
  private readonly _$isExistingContact: WritableSignal<boolean> = signal<boolean>(false);
  private readonly _$hasNewUserSubmittedForm: WritableSignal<boolean> = signal<boolean>(false);
  private readonly _$hasUserTriggeredDocusignWorkflow: WritableSignal<boolean> = signal<boolean>(false);
  readonly contactCreated: OutputEmitterRef<NewContact> = output<NewContact>();
  readonly modalClosed: OutputEmitterRef<void> = output<void>();
  private _account: Account | null = null;
  private _adminContact: SessionAuth | null = null;
  protected newContact: NewContact = {
    businessSegments: [],
    email: '',
    esaaStatus: 'pending',
    id: '',
    isExistingContact: false,
    name: { first: '', full: '', last: '' },
    segments: []
  };
  protected newContactForm: FormGroup;
  protected readonly $accountHasSignedNewEsaa: Signal<boolean> = this._$hasAccountSignedNewEsaa.asReadonly();
  protected readonly $accountHasSentNewEsaa: Signal<boolean> = this._$hasAccountSentNewEsaa.asReadonly();
  protected readonly $esaaSigner = this._$esaaSigner.asReadonly();
  protected readonly $existingUserHasSubmittedForm: Signal<boolean> = this._$hasExistingUserSubmittedForm.asReadonly();
  protected readonly $newUserHasSubmittedForm: Signal<boolean> = this._$hasNewUserSubmittedForm.asReadonly();
  protected readonly $userHasTriggeredDocusignWorkflow: Signal<boolean> =
    this._$hasUserTriggeredDocusignWorkflow.asReadonly();
  businessSegments: string[] = [];

  constructor() {
    this._storageSvc.currentAccount$.subscribe(account => this._onSessionDefined('account', account));
    this._storageSvc.currentUser$.subscribe(user => this._onSessionDefined('user', user));
  }

  /**
   * @description Method handles the caching of Session data for the logged-in
   *   Contact (Admin)
   * @param key 'account'|'user'
   * @param sessionData `SessionAuth`|`Account`
   */
  private _onSessionDefined(key: 'account' | 'user', sessionData: Account | SessionAuth | null): void {
    if (!sessionData) {
      return;
    }
    if (key === 'account') {
      this._account = sessionData as Account;
      if (!this._account.IsEsaaSigned) {
        this._$hasAccountSignedNewEsaa.set(false);
        if (this._account.EsaaStatus === 'Sent') {
          this.newContact = {
            ...this.newContact,
            esaaStatus: 'no'
          };
          this._$hasAccountSentNewEsaa.set(true);
          this._$esaaSigner.set(
            this.$accountContacts().find(accountContact => {
              if (accountContact.status.onboard === 'esaa sent') {
                return accountContact?.docusignId === this._account?.DocuSignEnvelopeId;
              }
            }) ?? null
          );
        }
      } else {
        this.newContact = {
          ...this.newContact,
          esaaStatus: 'no'
        };
      }
    } else if (key === 'user') {
      this._adminContact = sessionData as SessionAuth;
    }
  }

  protected get accountEsaaStatus(): string | undefined {
    return this._account?.EsaaStatus;
  }

  protected get accountName(): string | undefined {
    return this._account?.AccountName;
  }

  protected get contactPortalRoles(): FormArray {
    return this.newContactForm.get('portalRoles') as FormArray;
  }

  ngOnInit(): void {
    this.newContactForm = this._formBuilder.group({
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      email: [
        '',
        [
          Validators.required,
          Validators.pattern(
            /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/i
          ),
          EmailExistsValidator(this.$accountContacts().map(({ email }) => ({ email })))
        ]
      ],
      portalRoles: this._formBuilder.array([], [ContainsOneValidator()])
    });
    this.$accountRoles().forEach(role => {
      const roleControl = new FormControl<boolean>(false) as AdminFormControl;
      roleControl.id = role.id;
      (this.newContactForm.get('portalRoles') as FormArray).push(roleControl);
    });
  }

  sendDocuSignESAAToAccountAdmin(clickEvt?: MouseEvent): void {
    if (!this._account || !this._adminContact || !clickEvt?.target) {
      return;
    }
    const account = this._account;
    const target = clickEvt.target as HTMLElement;
    target.classList.add('is-waiting');
    const dsToken = this._docusignSvc.getAccessTokenFromCookie();
    //map data into expected envelope format
    const envData = this._docusignSvc.mapRecipientContactObjectIntoEnvelope(this._adminContact);
    if (envData) {
      if (dsToken === '') {
        this._docusignSvc
          .postDocuSignAuthenticate(account.AccountId)
          .pipe(
            switchMap(result => {
              this._docusignSvc.setDocuSignAccessToken(result);
              return this._docusignSvc.postDocuSignEnvelope(envData, account.AccountId);
            })
          )
          .subscribe({
            next: response => {
              target.classList.remove('is-waiting');
              this._$hasUserTriggeredDocusignWorkflow.set(true);
              this.persistEsaaAccountAndContactStatus(envData.SignerEmail, response.EnvelopeId);
            },
            error: () => {
              // this._toastSvc.dismissAll();
              target.classList.remove('is-waiting');
              setTimeout(() => {
                /*this._toastSvc.alert(
                  'DocuSign Error',
                  'We were unable to execute the DocuSign workflow for the desired user.'
                );*/
              }, 1000);
            }
          });
      } else {
        this._docusignSvc.postDocuSignEnvelope(envData, account.AccountId).subscribe({
          next: response => {
            target.classList.remove('is-waiting');
            this._$hasUserTriggeredDocusignWorkflow.set(true);
            this.persistEsaaAccountAndContactStatus(envData.SignerEmail, response.EnvelopeId);
          },
          error: () => {
            // this._toastSvc.dismissAll();
            target.classList.remove('is-waiting');
            setTimeout(() => {
              /*this._toastSvc.alert(
                'DocuSign Error',
                'We were unable to execute the DocuSign workflow for the desired user.'
              );*/
            }, 1000);
          }
        });
      }
    }
  }

  onNewContactSubmission(
    { email, firstName, lastName, portalRoles }: NewContactFormValue,
    submitEvt: SubmitEvent
  ): void {
    if (!this._account || !this._adminContact) {
      return;
    }
    const account = this._account;
    const adminContact = this._adminContact;
    const submitter = submitEvt.submitter as HTMLButtonElement | null;
    if (submitter) {
      submitter.classList.add('is-waiting');
      submitter.disabled = true;
    }
    posthog.capture('admin:add_new_user_submit', {
      email,
      name: {
        first: firstName,
        last: lastName
      },
      portal_role_names: this.$accountRoles()
        .filter((portalRole, portalRoleIdx) => {
          if (portalRoles[portalRoleIdx]) {
            return portalRole;
          }
        })
        .map(portalRole => portalRole.name)
    });
    this.businessSegments = this.setBusinessSegmentAssociationByPortalRoles(portalRoles);
    this._contactSvc
      .addContactToAccount(account.AccountId, {
        FirstName: firstName,
        LastName: lastName,
        Email: email,
        BusinessSegments: this.businessSegments
      })
      .subscribe({
        next: (response: AddContactResponse) => {
          this._$isExistingContact.set(
            response.UserMessage === 'The Account association was added to an existing contact'
          );
          if (response.ContactId) {
            const apiRequests: Observable<any>[] = [];
            const CONTACT_PORTAL_ROLES = ((): any => {
              const NEW_CONTACT_ROLES: { name: string; list: SanitizedPortalRole[]; visible: boolean }[] = [];
              const isNgpRolesExist = this.$accountRoles().some(
                accountPortalRole => accountPortalRole.name.toLowerCase() === 'requests'
              );
              const isNggpRolesExist = this.$accountRoles().some(
                accountPortalRole => !['requests', 'account access'].includes(accountPortalRole.name.toLowerCase())
              );
              if (isNgpRolesExist) {
                NEW_CONTACT_ROLES.push({
                  name: 'Pipelines',
                  list: this.$accountRoles()
                    .filter(accountPortalRole => accountPortalRole.name.toLowerCase() === 'requests')
                    .map(accountNgpPortalRole => {
                      accountNgpPortalRole.selected = portalRoles.some((isSelected: boolean, idx: number) => {
                        if (isSelected) {
                          return (
                            ((this.newContactForm.controls.portalRoles as FormArray).at(idx) as AdminFormControl).id ===
                            accountNgpPortalRole.id
                          );
                        }
                      });
                      return accountNgpPortalRole;
                    }),
                  visible: true
                });
              }
              if (isNggpRolesExist) {
                NEW_CONTACT_ROLES.push({
                  name: 'Gathering & Processing',
                  list: this.$accountRoles()
                    .filter(
                      accountPortalRole =>
                        !['requests', 'account access'].includes(accountPortalRole.name.toLowerCase())
                    )
                    .map(accountNggpPortalRole => {
                      accountNggpPortalRole.selected = portalRoles.some((isSelected: boolean, idx: number) => {
                        if (isSelected) {
                          return (
                            ((this.newContactForm.controls.portalRoles as FormArray).at(idx) as AdminFormControl).id ===
                            accountNggpPortalRole.id
                          );
                        }
                      });
                      return accountNggpPortalRole;
                    }),
                  visible: true
                });
              }
              return NEW_CONTACT_ROLES.sort(({ name: nameA }, { name: nameB }) =>
                nameA < nameB ? -1 : nameA > nameB ? 1 : 0
              );
            })();
            this.newContact = {
              ...this.newContact,
              name: {
                first: firstName,
                last: lastName,
                full: `${firstName} ${lastName}`
              },
              email,
              id: response.ContactId,
              isExistingContact: this._$isExistingContact(),
              segments: CONTACT_PORTAL_ROLES,
              businessSegments: CONTACT_PORTAL_ROLES.filter(({ list }: any): any =>
                list.some((role: any): any => role.selected)
              ).map(({ name }: any): any => (name === 'Gathering & Processing' ? 'nggp' : 'pipelines'))
            };
            portalRoles.forEach((isRoleSelected: boolean, roleIdx: number) => {
              if (isRoleSelected) {
                apiRequests.push(
                  this._contactSvc.addRoleToAccountContact.apply(this._contactSvc, [
                    account.AccountId,
                    email,
                    this.$accountRoles()[roleIdx].id
                  ])
                );
              }
            });
            forkJoin(apiRequests).subscribe({
              next: () => {
                //if new user then create and send a portal invitation on behalf of account
                if (!this._$isExistingContact()) {
                  this._registerSvc
                    .sendInvitationToAccountContact(
                      this.newContact.id as string,
                      account.AccountId,
                      this.newContact.name?.first ?? '',
                      this.newContact.name?.last ?? '',
                      adminContact.Contact?.Id ?? ''
                    )
                    .subscribe({
                      next: () => {
                        this.contactCreated.emit(this.newContact as NewContact);
                        this.newContactForm.reset();
                        this._$hasNewUserSubmittedForm.set(true);
                        submitter?.classList.remove('is-waiting');
                        if (submitter) {
                          submitter.disabled = false;
                        }
                      },
                      error: () => {
                        // this._toastSvc.dismissAll();
                        this._$hasNewUserSubmittedForm.set(true);
                        submitter?.classList.remove('is-waiting');
                        if (submitter) {
                          submitter.disabled = false;
                        }
                        setTimeout(() => {
                          /*this._toastSvc.alert(
                            'Invitation Request',
                            'The invitation couldn\'t be sent to this new user.', {
                              shouldShowServiceDesk: true
                            }
                          );*/
                        }, 1000);
                      }
                    });
                } else {
                  this.contactCreated.emit(this.newContact as NewContact);
                  this.newContactForm.reset();
                  this._$hasExistingUserSubmittedForm.set(true);
                  submitter?.classList.remove('is-waiting');
                  if (submitter) {
                    submitter.disabled = false;
                  }
                  this._$isExistingContact.set(false);
                }
              },
              error: () => {
                submitter?.classList.remove('is-waiting');
                // this._toastSvc.dismissAll();
                setTimeout(() => {
                  /*this._toastSvc.alert(
                    'User Role(s)',
                    'The requested role(s) couldn\'t be added to your new user', {
                      shouldShowServiceDesk: true
                    }
                  );*/
                }, 1000);
              }
            });
          }
        },
        error: () => {
          submitter?.classList.remove('is-waiting');
          // this._toastSvc.dismissAll();
          setTimeout(() => {
            /*this._toastSvc.alert(
              'New User',
              'Your new user couldn\'t be added.', {
                shouldShowServiceDesk: true
              }
            );*/
          }, 1000);
        }
      });
  }

  setBusinessSegmentAssociationByPortalRoles(portalRoles: boolean[]): string[] {
    const segmentsBasedOnRoles: string[] = [];
    const rolesSelected = portalRoles;

    this.$accountRoles().forEach((portalRole, idx) => {
      if (rolesSelected[idx]) {
        if (portalRole.name.toLowerCase() === 'requests') {
          segmentsBasedOnRoles.push('NGPIntrastate');
        } else {
          if (!segmentsBasedOnRoles.includes('NGGP')) {
            segmentsBasedOnRoles.push('NGGP');
          }
        }
      }
    });

    return segmentsBasedOnRoles;
  }

  isGrandfatherEsaaComponentShown(): boolean {
    if (this._permissionsSvc.hasPortalAdminRole()) {
      return false;
    }

    return !this.$accountHasSignedNewEsaa() && !this.$userHasTriggeredDocusignWorkflow();
  }

  isAddNewUserFormShown(): boolean {
    return (
      !this.$newUserHasSubmittedForm() &&
      !this.$existingUserHasSubmittedForm() &&
      !this.isGrandfatherEsaaComponentShown() &&
      !this.$userHasTriggeredDocusignWorkflow()
    );
  }

  clearFormSubmission(): void {
    this._$hasNewUserSubmittedForm.set(false);
    this._$hasExistingUserSubmittedForm.set(false);
    this._$hasUserTriggeredDocusignWorkflow.set(false);
    this._$isExistingContact.set(false);
  }

  findEsaaSignerForAccount(acct: Account): SanitizedContact | null {
    return (
      this.$accountContacts().find(contact => {
        if (contact.status.onboard === 'esaa sent') {
          return contact.docusignId === acct.DocuSignEnvelopeId;
        }
      }) ?? null
    );
  }

  persistEsaaAccountAndContactStatus(signerEmail: string, envelopeId: string): void {
    if (!this._account) {
      return;
    }
    this._account.EsaaStatus = 'Sent';
    this._account.DocuSignEnvelopeId = envelopeId;
    this._storageSvc.storeCurrentAccount(this._account);

    this.$accountContacts().forEach(contact => {
      if (contact.email === signerEmail) {
        contact.docusignId = envelopeId;
        contact.status.onboard = 'esaa sent';
      }
    });
  }
}
